import { useEffect, useMemo, useState } from 'react';
import * as React from 'react';

import Button from '@/components/elements/Button/BaseButton';
import Fingerprint from '@/components/elements/Fingerprint';
import AutoChargeSelect from '@/components/layouts/Modal/BetPanel/Partials/ConfirmBox/AutoCharge';
import ErrorModal from '@/components/layouts/Modal/Error';
import { TOKUSHOU_TERMS_URL } from '@/config/constants/terms';
import { useUserContext } from '@/contexts/user';
import { useChargeToretaFromWalletWithValidateOrderMutation } from '@/lib/apollo/types/gen/graphql';
import { sendToretaChargeEvent } from '@/lib/dmm/trackingTag';
import { ChargeToretaLabel } from '@/lib/dmm/trackingTag/type';
import { OrderStep } from '@/modelTypes';

import ReadyButton from './ReadyButton';
import {
  AutoChargeSelectWrapper,
  BeforeBetConfirmText,
  ButtonWrapper,
  ConfirmArea,
  ConfirmText,
  Detail,
  LinkText,
  Title,
} from './style';

interface ConfirmBoxProps {
  orderStep: OrderStep;
  setOrderStep: React.Dispatch<React.SetStateAction<OrderStep>>;
  totalBetCount: number;
  totalPrice: number;
  purchaseTickets: (userDevice: string) => void;
  disabled: boolean;
  isProcessing: boolean;
}

const ConfirmBox: React.FC<ConfirmBoxProps> = ({
  orderStep,
  setOrderStep,
  totalBetCount,
  totalPrice,
  purchaseTickets,
  disabled,
  isProcessing,
}) => {
  const { user, browser, device } = useUserContext();

  // その場でチャージ
  /// 不足金額
  const insufficientBalance = useMemo(() => {
    if (!user.wallet) {
      return 0;
    }
    return totalPrice - user.wallet.toreta.balance;
  }, [totalPrice, user.wallet?.toreta.balance]);

  // その場でチャージ設定（入金/払戻金 残高）
  const [isAutoChargeByCash, setIsAutoChargeByCash] = useState<boolean>(false);
  const [isAutoChargeByOther, setIsAutoChargeByOther] =
    useState<boolean>(false);

  const [isAutoChargeProcessing, setIsAutoChargeProcessing] =
    useState<boolean>(false);
  const [userDevice, setUserDevice] = useState<string>('');

  // オートチャージ額（クレカ決済の場合は最低チャージ金額があるため）
  const autoChargeAmount = useMemo(() => {
    return insufficientBalance;
  }, [insufficientBalance]);

  // 不足金額がウォレット残高を上回ったら、ウォレットからのその場でチャージをOFFにする
  useEffect(() => {
    if (user.wallet && user.wallet.balance < insufficientBalance) {
      setIsAutoChargeByCash(false);
      setIsAutoChargeByOther(false);
    }
  }, [insufficientBalance]);

  // 入金/清算金残高 toreta+チャージMutation
  const [chargeToretaFromWalletMutation, chargeToretaFromWalletMutationResult] =
    useChargeToretaFromWalletWithValidateOrderMutation();
  const [isOpenErrorModal, setIsOpenErrorModal] = useState<boolean>(false);

  // 確定ボタン押下後 その場でチャージ実行/購入 のハンドリング
  const handleConfirm = () => {
    if (isProcessing || isAutoChargeProcessing || disabled) {
      return;
    }

    // その場でチャージ有効
    if (isAutoChargeByCash) {
      setIsAutoChargeProcessing(true);
      // ウォレット その場でチャージ
      // 生産可能額→toreta+ チャージ後に購入実行
      if (isAutoChargeByCash) {
        chargeToretaFromWalletMutation({
          variables: {
            amount: insufficientBalance,
            orderAmount: totalPrice,
            browser: browser,
            device: device,
          },
        })
          .then(res => {
            if (res.data) {
              setIsAutoChargeProcessing(false);
              sendToretaChargeEvent(ChargeToretaLabel.PreChargeFromWallet);
              purchaseTickets(userDevice);
              return Promise.resolve();
            }
            return Promise.reject();
          })
          .catch(() => {
            setIsAutoChargeProcessing(false);
            setIsOpenErrorModal(true);
          });
        return;
      }
    }

    // その場でチャージ無効 購入実行
    purchaseTickets(userDevice);
  };

  // 自動チャージ成功後に投票が失敗したとき自動チャージを解除する処理
  useEffect(() => {
    if (
      isAutoChargeByCash &&
      orderStep === OrderStep.Ready &&
      insufficientBalance <= 0
    ) {
      setIsAutoChargeByCash(false);
    }
  }, [isAutoChargeByCash, orderStep, insufficientBalance]);

  return (
    <ConfirmArea>
      {orderStep === OrderStep.Confirm && (
        <ConfirmText>
          <p data-testid="confirm-text">投票確認</p>
          投票パネルの内容で投票します。
          <br />
          投票した内容は取り消しできません。
        </ConfirmText>
      )}
      <Detail>
        <Title>組数</Title>
        <span data-testid="total-bet-count">
          {totalBetCount.toLocaleString()}組
        </span>
      </Detail>
      <Detail>
        <Title>投票額</Title>
        <span data-testid="total-bet-price">
          {totalPrice.toLocaleString()}円
        </span>
      </Detail>
      {orderStep === OrderStep.Confirm &&
        insufficientBalance > 0 &&
        isAutoChargeByCash && (
          <Detail>
            <Title data-testid="charge-type-name">
              toreta+ チャージ
              <br />
              {isAutoChargeByCash && '(入金/払戻金 残高)'}
            </Title>
            <span data-testid="auto-charge-amount">
              {autoChargeAmount.toLocaleString()}円分
            </span>
          </Detail>
        )}

      {/* その場でチャージ選択 */}
      {orderStep === OrderStep.Ready && insufficientBalance > 0 && (
        <AutoChargeSelectWrapper>
          <AutoChargeSelect
            insufficientBalance={insufficientBalance}
            isAutoChargeByCash={isAutoChargeByCash}
            isAutoChargeByOther={isAutoChargeByOther}
            setIsAutoChargeByCash={setIsAutoChargeByCash}
            setIsAutoChargeByOther={setIsAutoChargeByOther}
          />
        </AutoChargeSelectWrapper>
      )}
      {orderStep === OrderStep.Ready && (
        <>
          <ReadyButton
            disabled={
              disabled ||
              (insufficientBalance > 0 &&
                !isAutoChargeByCash &&
                !isAutoChargeByOther)
            }
            isAutoChargeByOther={isAutoChargeByOther}
            setOrderStep={setOrderStep}
          />
        </>
      )}
      {orderStep === OrderStep.Confirm && (
        <>
          <BeforeBetConfirmText>
            <p>
              投票前にご確認ください。
              <br />
              <LinkText>
                <a href={`${TOKUSHOU_TERMS_URL}#payment`} target="_blank">
                  代金の支払い時期
                </a>
                、
                <a href={`${TOKUSHOU_TERMS_URL}#available`} target="_blank">
                  商品等の引き渡し時期
                </a>
                、
                <a href={`${TOKUSHOU_TERMS_URL}#return`} target="_blank">
                  投票の変更・取り消し
                </a>
              </LinkText>
              車券の投票はtoreta+ のみで行えます。
              <br />
              {isAutoChargeByCash && (
                <>
                  締切直前に投票した場合、toreta+のチャージは成功しても投票に失敗してしまうことがあります。
                  <br />
                  toreta+の返金は行えませんのでご注意ください。
                  <br />
                </>
              )}
            </p>
          </BeforeBetConfirmText>
          <ButtonWrapper>
            <Button
              size="m"
              bgColor="primary"
              fontWeight="bold"
              height="56px"
              radius="l"
              isLoading={isProcessing || isAutoChargeProcessing}
              disabled={isProcessing || isAutoChargeProcessing || disabled}
              onClick={() => handleConfirm()}
            >
              確定する
            </Button>
            <Button
              outline
              size="m"
              bgColor="darkGray"
              fontWeight="bold"
              height="56px"
              radius="l"
              disabled={isProcessing || isAutoChargeProcessing || disabled}
              onClick={() => setOrderStep(OrderStep.Ready)}
            >
              キャンセル
            </Button>
          </ButtonWrapper>
        </>
      )}
      <ErrorModal
        isOpen={isOpenErrorModal}
        handleClose={() => setIsOpenErrorModal(false)}
        apolloError={chargeToretaFromWalletMutationResult.error}
      />
      <Fingerprint setState={setUserDevice} />
    </ConfirmArea>
  );
};

export default ConfirmBox;
